/**
 * The generated stab bank — the scratch scene's default material.
 *
 * There is no sample here and no sample is fetched: every stab is rendered
 * once, at start, into an OfflineAudioContext. That keeps the scene playable
 * with zero assets and zero licence questions, which is the whole reason the
 * vocal-style stabs are synthesised formants rather than a recording.
 *
 * Formant tables are the standard vowel triples (F1/F2/F3 with relative
 * weights); the vowel voice is a saw through three bandpasses, which is the
 * cheapest thing that reads as a voice at scratch rates.
 */

export type StabId = 'ahh' | 'eee' | 'ooh' | 'zap' | 'horn';

/** [frequency Hz, relative gain] x3 — F1, F2, F3. */
export const FORMANTS: Record<'ahh' | 'eee' | 'ooh', [number, number][]> = {
  ahh: [[730, 0.7], [1090, 0.4], [2440, 0.16]],
  eee: [[270, 0.7], [2290, 0.4], [3010, 0.16]],
  ooh: [[300, 0.7], [870, 0.35], [2240, 0.12]],
};

export const VOWEL_DUR = 0.7;
export const ZAP_DUR = 0.5;
export const HORN_DUR = 0.7;
export const VOWEL_F0 = 130;
export const VIBRATO_HZ = 5.5;
export const VIBRATO_DEPTH = 3; // Hz
export const FORMANT_Q = 8;
export const HORN_SEMITONES = [0, 4, 7, 12];
export const HORN_BP_HZ = 1150;
export const HORN_BP_Q = 1.1;

export const STAB_IDS: StabId[] = ['ahh', 'eee', 'ooh', 'zap', 'horn'];

export const STAB_LABELS: Record<StabId, string> = {
  ahh: 'AHH', eee: 'EEE', ooh: 'OOH', zap: 'ZAP', horn: 'HORN',
};

export const stabDuration = (id: StabId): number =>
  id === 'zap' ? ZAP_DUR : id === 'horn' ? HORN_DUR : VOWEL_DUR;

type OfflineCtor = new (channels: number, length: number, sampleRate: number) => OfflineAudioContext;

function offline(sampleRate: number, seconds: number): OfflineAudioContext {
  const Ctor = (window.OfflineAudioContext ??
    (window as unknown as { webkitOfflineAudioContext: OfflineCtor }).webkitOfflineAudioContext) as OfflineCtor;
  return new Ctor(1, Math.ceil(sampleRate * seconds), sampleRate);
}

/** Fast attack, short hold, exponential tail. One shape for every stab. */
function shape(ctx: BaseAudioContext, dur: number, attack = 0.018, hold = 0.1): GainNode {
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, 0);
  g.gain.linearRampToValueAtTime(1, attack);
  g.gain.setValueAtTime(1, attack + hold);
  g.gain.exponentialRampToValueAtTime(0.0001, dur);
  return g;
}

function noiseBuffer(ctx: BaseAudioContext, seconds: number, seed = 0x5eed1e): AudioBuffer {
  let s = seed;
  const rnd = (): number => {
    s |= 0; s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const buf = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * seconds), ctx.sampleRate);
  const ch = buf.getChannelData(0);
  for (let i = 0; i < ch.length; i++) ch[i] = rnd() * 2 - 1;
  return buf;
}

function renderVowel(sampleRate: number, id: 'ahh' | 'eee' | 'ooh'): Promise<AudioBuffer> {
  const ctx = offline(sampleRate, VOWEL_DUR);
  const src = ctx.createOscillator();
  src.type = 'sawtooth';
  src.frequency.value = VOWEL_F0;

  // the vibrato — 5.5 Hz, +/-3 Hz on the fundamental
  const lfo = ctx.createOscillator();
  lfo.frequency.value = VIBRATO_HZ;
  const dep = ctx.createGain();
  dep.gain.value = VIBRATO_DEPTH;
  lfo.connect(dep); dep.connect(src.frequency);

  const amp = shape(ctx, VOWEL_DUR);
  for (const [f, g] of FORMANTS[id]) {
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = f;
    bp.Q.value = FORMANT_Q;
    const fg = ctx.createGain();
    fg.gain.value = g;
    src.connect(bp); bp.connect(fg); fg.connect(amp);
  }
  amp.connect(ctx.destination);
  src.start(0); src.stop(VOWEL_DUR);
  lfo.start(0); lfo.stop(VOWEL_DUR);
  return ctx.startRendering();
}

function renderZap(sampleRate: number): Promise<AudioBuffer> {
  const ctx = offline(sampleRate, ZAP_DUR);
  const amp = shape(ctx, ZAP_DUR, 0.004, 0.02);
  const o = ctx.createOscillator();
  o.type = 'sawtooth';
  o.frequency.setValueAtTime(1500, 0);
  o.frequency.exponentialRampToValueAtTime(160, ZAP_DUR * 0.8);
  const og = ctx.createGain();
  og.gain.value = 0.8;
  o.connect(og); og.connect(amp);

  // the transient — a short filtered noise crack on the front
  const n = ctx.createBufferSource();
  n.buffer = noiseBuffer(ctx, 0.06);
  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 1200;
  const ng = ctx.createGain();
  ng.gain.setValueAtTime(0.9, 0);
  ng.gain.exponentialRampToValueAtTime(0.0001, 0.05);
  n.connect(hp); hp.connect(ng); ng.connect(amp);

  amp.connect(ctx.destination);
  o.start(0); o.stop(ZAP_DUR);
  n.start(0); n.stop(0.06);
  return ctx.startRendering();
}

function renderHorn(sampleRate: number): Promise<AudioBuffer> {
  const ctx = offline(sampleRate, HORN_DUR);
  const amp = shape(ctx, HORN_DUR, 0.018, 0.18);
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = HORN_BP_HZ;
  bp.Q.value = HORN_BP_Q;
  const base = 220;
  HORN_SEMITONES.forEach((s, i) => {
    for (const det of [-6, 6]) {
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = base * Math.pow(2, s / 12);
      o.detune.value = det;
      const g = ctx.createGain();
      g.gain.value = 0.3 / (1 + i * 0.35);
      o.connect(g); g.connect(bp);
      o.start(0); o.stop(HORN_DUR);
    }
  });
  bp.connect(amp); amp.connect(ctx.destination);
  return ctx.startRendering();
}

export function renderStab(sampleRate: number, id: StabId): Promise<AudioBuffer> {
  if (id === 'zap') return renderZap(sampleRate);
  if (id === 'horn') return renderHorn(sampleRate);
  return renderVowel(sampleRate, id);
}

export async function renderAllStabs(sampleRate: number): Promise<Record<StabId, AudioBuffer>> {
  const out = {} as Record<StabId, AudioBuffer>;
  for (const id of STAB_IDS) out[id] = await renderStab(sampleRate, id);
  return out;
}
